import Clouds from '@/common/Clouds';
import { MainHeader, SectionParagraph } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import { PartnerLogo } from '@/components/shared/PartnerLogo';
import { useShowHeaderCtaOnScroll } from '@/hooks/useShowHeaderCtaOnScroll';
import { PRODUCT_LIFE } from '@/utils/products';
import { Flex, LegacyText, preventOneWordInLastLine } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import type React from 'react';
import styled from 'styled-components';
import FamilyAnimation from './FamilyAnimation';

const DESKTOP_HEADER_HEIGHT = 100;
const DESKTOP_TOP_SECTION_MARGIN = 120;
const MOBILE_HEADER_HEIGHT = 70;
const MOBILE_TOP_SECTION_MARGIN = 35;
const MOBILE_TOP_SECTION_HEIGHT = `calc(100vh - ${MOBILE_TOP_SECTION_MARGIN + MOBILE_HEADER_HEIGHT}px)`;
const DESKTOP_TOP_SECTION_HEIGHT = `calc(100vh - ${DESKTOP_TOP_SECTION_MARGIN + DESKTOP_HEADER_HEIGHT}px)`;

const StyledClouds = styled(Clouds)`
  transform: scale(2);
  z-index: 0;

  ${MEDIA_QUERIES.tabletAndDesktop`
        transform: scale(1.5);
  `}
  ${MEDIA_QUERIES.mobile`
        left: auto;
        right: 50%;
  `}
`;

const StyledPartnerLogo = styled(PartnerLogo)`
  margin-top: -50px;
  margin-bottom: 0;
  ${MEDIA_QUERIES.desktop`
    margin-top: -110px;
    margin-bottom: 40px;
  `}
`;

export interface HeroProps {
  title: string;
  subtitle: string;
  partnerLogo?: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

export const Hero: React.FC<HeroProps> = ({ title, subtitle, partnerLogo, ctaLabel = 'Check our prices', ctaUrl }) => {
  const { ref: mainCtaRef } = useShowHeaderCtaOnScroll();
  return (
    <>
      <StyledClouds top={[160, 200, 230]} />
      <Flex
        position="relative"
        flexDirection="column"
        alignItems="center"
        minHeight={[650, 650, 690]}
        height={[MOBILE_TOP_SECTION_HEIGHT, MOBILE_TOP_SECTION_HEIGHT, DESKTOP_TOP_SECTION_HEIGHT]}
      >
        <StyledPartnerLogo src={partnerLogo} />
        <Flex flexDirection="column" alignItems="center">
          <Flex width={[1, 1, 760]} mb="20px">
            <MainHeader tabIndex={0}>{preventOneWordInLastLine(title)}</MainHeader>
          </Flex>
          <Flex mb={[40, 40, 60]}>
            <SectionParagraph tabIndex={0}>{subtitle}</SectionParagraph>
          </Flex>

          <JoinCTA
            size="big"
            flavor="primary"
            glowing
            data-source="life"
            product={PRODUCT_LIFE}
            minWidth={233}
            data-position="top"
            ref={mainCtaRef}
            customUrl={ctaUrl}
            aria-label={`${ctaLabel} Starting from $8/mo.`}
          >
            {ctaLabel}
          </JoinCTA>
          <LegacyText type="main" mt={[25, 25, 20]} fontSize="1.6rem" color="night">
            Starting from $8/mo.
          </LegacyText>
        </Flex>
      </Flex>
      <FamilyAnimation />
    </>
  );
};
